import React, { useEffect } from "react";
import styled from "styled-components";
import { AnimatePresence, LayoutGroup, MotionConfig, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { faThLarge, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { useAppSelector } from "../../../store/hooks";
import { Button } from "../../../ui/components/Button";
import AotdCardsPanel from "../../respectCards/aotd/AotdCardsPanel";
import AotdCardPlayProvider from "../../respectCards/aotd/AotdCardPlayContext";
import { checkRole } from "../../../components/recipe_page/recipe_page";
import AotdIrlPublicFramesSection from "../../../components/art_of_the_day/AotdIrlPublicFramesSection";
import AOTDCalendarBoard from "../sections/calendar/AotdCalendarBoard";
import ArtOfTheDayAdminPanel from "../sections/admin";
import PingFooter from "../components/shared/PingFooter";
import { useAotdRace } from "../hooks/useAotdRace";
import { useAotdReveal } from "../hooks/useAotdReveal";
import { useStreakIcon } from "../hooks/useStreakIcon";
import { useWaybackPolling } from "../hooks/useWaybackPolling";
import UiSwitch from "../components/UiSwitch";
import type { UiState } from "../state/types";
import "../styles/spinner.css";
import "../styles/aotd.css";

const ArtOfTheDayPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f0f0f0;
  padding: 15px;
  height: auto;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #222;
  text-align: center;
  margin-bottom: 1rem;
`;

// The page is a column of blocks; these wrappers keep that column layout so
// the blocks can animate (screen swaps fade, everything below glides).
const Column = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/** One key per screen. "connecting" → "loading" is a single screen, not a swap. */
function screenKey(ui: UiState): string {
  return ui.kind === "connecting" ? "loading" : ui.kind;
}

const Description = styled.p`
  font-size: 0.8rem;
  color: #7f7f7f;
  max-width: 800px;
  text-align: center;
  margin-bottom: 2rem;
`;

export const ArtOfTheDayPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth);
  const isAuthenticated = user.bootstrapStatus === "ready" && Boolean(user.token);
  const race = useAotdRace();
  const streakIcon = useStreakIcon();

  const completedArt = race.ui.kind === "completed" ? race.ui.art : null;
  useWaybackPolling({
    enabled: race.ui.kind === "completed",
    art: completedArt,
    refetch: race.refreshInfo,
  });

  useEffect(() => {
    document.title = "NΛSOS Art Of The Day";
  }, []);

  const aotdId = race.ui.kind === "completed" ? race.ui.art.aotdId ?? null : null;
  // today's art screen waits for its image, the hand and the quiz, then shows at once
  const shown = useAotdReveal(race.ui, {
    isAuthenticated,
    authReady: user.bootstrapStatus === "ready",
  });
  const loading = screenKey(shown) === "loading";

  return (
    <ArtOfTheDayPageContainer>
      {/* One shared Play modal for every card affordance on this page — the
          panel's hand row and the comments' Anonymous-card hint. */}
      <AotdCardPlayProvider aotdId={aotdId} isAuthenticated={isAuthenticated}>
      <MotionConfig reducedMotion="user">
      <Button
        mobileSlot="special1"
        icon={faRotateRight}
        bgColor="#ffffff"
        textColor="#232323"
        borderColor="#232323"
        loading={race.refreshing}
        disabled={race.refreshBlocked}
        onClick={race.refresh}
        aria-label="refresh art of the day"
      >
        aotd
      </Button>
      <Button
        mobileSlot="special2"
        icon={faThLarge}
        bgColor="#dec8f5"
        textColor="#663399"
        borderColor="#663399"
        onClick={() => navigate("/art-of-the-day/gallery")}
      >
        gal
      </Button>
      <Title>Art of the Day</Title>

      {/* the live-effects banner strip (AotdCardsPanel placement="banner") is
          hidden until the cards launch — the component is untouched */}

      <LayoutGroup>
      <AnimatePresence mode="wait" initial={false}>
        <Column
          key={screenKey(shown)}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <UiSwitch
            ui={shown}
            race={race}
            streakIcon={streakIcon}
            isAuthenticated={isAuthenticated}
            currentUsername={user.username ?? null}
          />
        </Column>
      </AnimatePresence>

      <Column layout="position" transition={{ duration: 0.3, ease: "easeOut" }}>
      {!loading && (
        // waits out the spinner's 0.25s exit, so it fades in with the screen that replaces it
        <Column initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.25 }}>
        <AotdCardsPanel
          aotdId={aotdId}
          isAuthenticated={isAuthenticated}
          placement="panel"
        />
        <Description>
          <br />
          <br />
          the art is created only when someone visits this page after 04:50 UTC,  (
          {(() => {
            const utcTime = new Date();
            utcTime.setUTCHours(4, 50, 0, 0);
            return utcTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
          })()}{" "}
          your local time) <strong>in 12 different timezones</strong>. whoever visits this page first
          becomes the author of the art.
        </Description>
        </Column>
      )}
      <AOTDCalendarBoard className="mt-4" />
      {checkRole(user.role || "", "admin") && <ArtOfTheDayAdminPanel />}
      <AotdIrlPublicFramesSection isAuthenticated={isAuthenticated} />
      <PingFooter />
      </Column>
      </LayoutGroup>
      </MotionConfig>
      </AotdCardPlayProvider>
    </ArtOfTheDayPageContainer>
  );
};

export default ArtOfTheDayPage;
